import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import SidebarPage from '../../../../layout/SidebarPage/SidebarPage';
import PageTitle from '../../../../layout/PageTitle/PageTitle';
import { Button, Skeleton, Tabs, Alert } from 'antd';
import { useGet } from '../../../../hooks/useApi';
import {
  StudentAdminResponse,
  StudentAdmin,
} from '../../../../models/admin/student-admin.model';
import BasicTab from './Tabs/BasicTab';
import ScheduleTab from './Tabs/ScheduleTab';
import NSDCampsTab from './Tabs/NSDCampsTab';
import AdditionalGuardiansTab from './Tabs/AdditionalGuardiansTab';
import EmergencyContactsTab from './Tabs/EmergencyContactsTab';
import InsuranceTab from './Tabs/InsuranceTab';
import MedicalTab from './Tabs/MedicalTab';
import MedicationTab from './Tabs/MedicationTab';
import SharedInfoTab from './Tabs/SharedInfoTab';
import LogsTab from './Tabs/LogsTab';

const StudentInfo: FC = () => {
  const { studentId } = useParams<{ studentId: string }>();

  const [student, setStudent] = useState<StudentAdmin | null>(null);
  const [studentData, setStudentData] = useState<StudentAdminResponse | null>(
    null
  );

  const api = useGet<StudentAdminResponse>(
    `/v1/auth/admin/students/${studentId}`,
    {
      enabled: !!studentId,
    }
  );

  useEffect(() => {
    if (api.isSuccess && api.data) {
      setStudent(api.data.student);
      setStudentData(api.data);
    }
  }, [api.isSuccess, api.data]);

  const OnError = () => (
    <Alert
      message="Error Loading Student"
      description="There was an error loading the student information. Please try again."
      type="error"
      showIcon
      action={
        <Button type="primary" onClick={() => api.refetch()}>
          Reload
        </Button>
      }
    />
  );

  if (api.isLoading) {
    return (
      <SidebarPage>
        <PageTitle>Loading...</PageTitle>
        <Skeleton active paragraph={{ rows: 10 }} />
      </SidebarPage>
    );
  }

  if (api.isError) {
    return (
      <SidebarPage>
        <PageTitle>Student Info</PageTitle>
        <OnError />
      </SidebarPage>
    );
  }

  const tabItems = [
    {
      key: '1',
      label: 'Basic',
      children: student && (
        <BasicTab student={student} onUpdate={() => api.refetch()} />
      ),
    },
    {
      key: '2',
      label: 'Schedule',
      children: studentData && (
        <ScheduleTab
          schedules={studentData.schedules}
          legacySchedules={studentData.legacySchedules}
          upcomingChanges={studentData.upcomingChanges}
          studentId={Number(studentId)}
        />
      ),
    },
    {
      key: '3',
      label: 'NSD Camps',
      children: studentData && (
        <NSDCampsTab
          enrollments={studentData.nsdCampEnrollments}
          studentId={Number(studentId)}
        />
      ),
    },
    {
      key: '4',
      label: 'Additional Guardians',
      children: studentData && (
        <AdditionalGuardiansTab guardians={studentData.additionalGuardians} />
      ),
    },
    {
      key: '5',
      label: 'Emergency Contacts',
      children: studentData && (
        <EmergencyContactsTab contacts={studentData.emergencyContacts} />
      ),
    },
    {
      key: '6',
      label: 'Insurance/Pediatrician',
      children: studentData && (
        <InsuranceTab insurance={studentData.insurance} />
      ),
    },
    {
      key: '7',
      label: 'Medical Info',
      children: studentData && (
        <MedicalTab medicalInfo={studentData.medicalInfo} />
      ),
    },
    {
      key: '8',
      label: 'Medication',
      children: studentData && (
        <MedicationTab medications={studentData.medications} />
      ),
    },
    {
      key: '9',
      label: 'Shared Info',
      children: studentData && (
        <SharedInfoTab sharingInfo={studentData.sharingInfo} />
      ),
    },
    {
      key: '10',
      label: 'Logs',
      children: student && <LogsTab studentId={student.id} />,
    },
  ];

  return (
    <SidebarPage>
      <PageTitle>
        {student ? `${student.firstName} ${student.lastName}` : 'Student Info'}
      </PageTitle>

      <Tabs defaultActiveKey="1" items={tabItems} />
    </SidebarPage>
  );
};

export default StudentInfo;
